import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Select, message } from "antd";
import Table from "../../../components/Table/Table";
import Pagination from "../../../components/Pagination/Pagination";
import ConfidencePill from "../../../components/ConfidencePill";
import { normalizeConfidence } from "../utils";
import {
  ListViewWrapper,
  ListToolbar,
  ListSearchInput,
  ListTableWrapper,
  ListFooter,
  StatusPill,
  MutedText,
  MetaText,
  ResultName,
} from "../MailIntakeStyled";
import {
  fetchQueueList,
  fetchSkippedList,
  setQueueFilters,
  claimSpecificItem,
  setActiveTab,
} from "../../../slice/MailIntakeSlice";

// Statuses where row-click should attempt a claim. Terminal statuses
// (assigned, deleted) and in-flight uploads have nothing the sorter can
// "open and assign", so we short-circuit client-side instead of making
// the user wait for a backend round-trip just to see a 409.
const CLICKABLE_STATUSES = new Set(["pending", "claimed", "skipped"]);

// Status options surfaced in the List tab dropdown. "Active" is the default
// and is a client-side sentinel the API layer translates into "no status
// param", which the backend now treats as the active queue (uploading +
// pending + claimed + skipped). "Assigned" and "Deleted" are explicit
// opt-ins for looking at terminal-state rows without mixing them into the
// working queue.
const STATUS_OPTIONS = [
  { value: "ACTIVE", label: "Active" },
  { value: "pending", label: "Pending" },
  { value: "claimed", label: "In Progress" },
  { value: "skipped", label: "Skipped" },
  { value: "assigned", label: "Assigned" },
  { value: "deleted", label: "Deleted" },
];

const formatDate = (d) => {
  if (!d) return "—";
  return new Date(d).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const formatSize = (bytes) => {
  if (!bytes) return "—";
  if (bytes < 1048576) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

// Human-readable labels for every status the backend can emit. Keeping
// this aligned with MailIntakeItem.Status on the server means newly-added
// statuses (e.g. "deleted") render as a real pill instead of falling
// through to the raw value.
const STATUS_LABELS = {
  uploading: "Uploading",
  pending: "Pending",
  claimed: "In Progress",
  skipped: "Skipped",
  assigned: "Assigned",
  deleted: "Deleted",
};

const EmptyCell = () => <MutedText>—</MutedText>;

const columns = [
  {
    title: "File Name",
    dataIndex: "original_filename",
    key: "original_filename",
    ellipsis: true,
    width: "25%",
    render: (_, record) => {
      // Prefer the user-editable document_name when present; fall back to
      // the raw upload filename so the column is never blank.
      const label = record.document_name || record.original_filename || "—";
      return <ResultName style={{ fontSize: 13 }}>{label}</ResultName>;
    },
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    width: 120,
    render: (status, record) => (
      <>
        <StatusPill status={status}>{STATUS_LABELS[status] || status}</StatusPill>
        {status === "skipped" && record.last_skip?.reason_label && (
          <MetaText style={{ display: "block", marginTop: 4 }}>
            {record.last_skip.reason_label}
          </MetaText>
        )}
      </>
    ),
  },
  {
    title: "Source",
    dataIndex: "source",
    key: "source",
    width: 110,
    render: (s) =>
      s ? (
        <MetaText style={{ textTransform: "capitalize" }}>
          {String(s).replace(/_/g, " ")}
        </MetaText>
      ) : (
        <EmptyCell />
      ),
  },
  {
    title: "Pages",
    dataIndex: "page_count",
    key: "page_count",
    width: 70,
    align: "center",
    // `v != null` (not truthy) so a genuine 0-page document renders as
    // "0" instead of "—". Truthiness would conflate "unknown" with
    // "zero pages" (Bugbot PR #47).
    render: (v) => (v != null ? v : <EmptyCell />),
  },
  {
    title: "Size",
    dataIndex: "file_size",
    key: "file_size",
    width: 90,
    render: formatSize,
  },
  {
    title: "Received",
    dataIndex: "created_at",
    key: "created_at",
    width: 160,
    render: formatDate,
  },
  {
    title: "Auto-Match",
    key: "auto_match",
    width: 200,
    render: (_, record) => {
      // The list serializer exposes auto_match_matter_name +
      // auto_match_confidence (0..1 float) as flat fields.
      const name = record.auto_match_matter_name;
      if (!name) return <MutedText>No match</MutedText>;
      return (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <ResultName
            style={{
              fontSize: 13,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {name}
          </ResultName>
          <ConfidencePill value={normalizeConfidence(record.auto_match_confidence)} />
        </div>
      );
    },
  },
  {
    title: "Assigned To",
    key: "assigned_matter",
    width: 200,
    render: (_, record) => {
      // `assigned_matter_name` is added by backend PR #52. Until that PR
      // lands we fall back to the raw matter id so the column is still
      // informative rather than blank. Once #52 is deployed the id fallback
      // simply stops being hit.
      const name = record.assigned_matter_name;
      if (name) {
        return <ResultName style={{ fontSize: 13 }}>{name}</ResultName>;
      }
      if (record.assigned_matter) {
        return <MetaText>Matter #{record.assigned_matter}</MetaText>;
      }
      return <EmptyCell />;
    },
  },
];

// `skippedFolder` turns this into the reviewers' Skipped tab: skipped mail
// only, with its own page, search and results (see fetchSkippedList).
const ListView = ({ skippedFolder = false } = {}) => {
  const dispatch = useDispatch();
  const {
    queue: listQueue,
    queueLoading: listLoading,
    queueFilters,
    skippedQueue,
    skippedQueueLoading,
  } = useSelector((state) => state.mailIntake);
  const searchTimerRef = useRef(null);
  const [localSearch, setLocalSearch] = useState(
    skippedFolder ? "" : queueFilters.search
  );
  const [skippedFilters, setSkippedFilters] = useState({ page: 1, search: "" });
  const queue = skippedFolder ? skippedQueue : listQueue;
  const queueLoading = skippedFolder ? skippedQueueLoading : listLoading;
  const currentPage = skippedFolder ? skippedFilters.page : queueFilters.page;

  useEffect(() => {
    if (skippedFolder) dispatch(fetchSkippedList(skippedFilters));
  }, [dispatch, skippedFolder, skippedFilters]);

  useEffect(() => {
    if (!skippedFolder) dispatch(fetchQueueList());
  }, [dispatch, skippedFolder, queueFilters]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setLocalSearch(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      if (skippedFolder) setSkippedFilters({ page: 1, search: value });
      else dispatch(setQueueFilters({ search: value, page: 1 }));
    }, 350);
  };

  const handleStatusChange = (value) => {
    dispatch(setQueueFilters({ status: value, page: 1 }));
  };

  const handlePageChange = (page) => {
    if (skippedFolder) setSkippedFilters((prev) => ({ ...prev, page }));
    else dispatch(setQueueFilters({ page }));
  };

  // Row click -> open the document in the Assign tab. Three outcomes:
  //   1. Status not actionable here (assigned / deleted / uploading):
  //      short-circuit with a toast explaining why.
  //   2. Backend rejects the claim (e.g., already claimed by another
  //      sorter, terminal state we missed, expired session): surface the
  //      backend's specific error message via toast and stay on this tab.
  //   3. Claim succeeds: the slice has stored the full item as
  //      currentItem; just switch to the Assign tab. We deliberately do
  //      NOT dispatch refreshIntakeCounts() here -- MailIntake.js's
  //      useEffect already re-fetches counts whenever activeTab changes,
  //      and firing it explicitly here would double the /counts/ request
  //      (Bugbot PR #47).
  const handleRowClick = async (record) => {
    if (!CLICKABLE_STATUSES.has(record.status)) {
      const reason =
        record.status === "assigned"
          ? "This document is already assigned to a matter."
          : record.status === "deleted"
          ? "This document was deleted."
          : "This document is still uploading.";
      message.info(reason);
      return;
    }
    const result = await dispatch(claimSpecificItem(record.id));
    if (claimSpecificItem.fulfilled.match(result)) {
      dispatch(setActiveTab("assign"));
    } else {
      message.error(result.payload || "Could not open this document");
    }
  };

  const results = queue?.results || [];
  const total = queue?.count || 0;

  return (
    <ListViewWrapper>
      <ListToolbar>
        <ListSearchInput
          placeholder="Search by file name or client..."
          value={localSearch}
          onChange={handleSearchChange}
        />
        {!skippedFolder && (
          <Select
            value={queueFilters.status}
            onChange={handleStatusChange}
            options={STATUS_OPTIONS}
            style={{ minWidth: 150, fontFamily: "Manrope" }}
            popupMatchSelectWidth={false}
          />
        )}
      </ListToolbar>

      <ListTableWrapper>
        <Table
          columns={columns}
          data={results}
          loading={queueLoading}
          emptyMessage={
            skippedFolder
              ? "Nothing in the Skipped folder"
              : "No documents in the intake queue"
          }
          onRow={(record) => ({
            onClick: () => handleRowClick(record),
            style: {
              cursor: CLICKABLE_STATUSES.has(record.status)
                ? "pointer"
                : "default",
            },
          })}
        />
      </ListTableWrapper>

      {total > 0 && (
        <ListFooter>
          <Pagination
            current={currentPage}
            total={total}
            pageSize={queueFilters.page_size}
            onChange={handlePageChange}
          />
        </ListFooter>
      )}
    </ListViewWrapper>
  );
};

export default ListView;
